import { useState } from 'react';
import { faGoogle } from '@awesome.me/kit-4a6bf60416/icons/classic/brands';
import { faStar as faStarRegular } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { faStar as faStarSolid } from '@awesome.me/kit-4a6bf60416/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { usePageSectionData } from '@/components/page-sections/page-section-context';
import { cn, getInitials } from '@/lib/utils';
import type { GoogleReview, GoogleReviewsSectionProps } from './definition';

const STAR_SLOTS = ['s1', 's2', 's3', 's4', 's5'] as const;
const MAX_STARS = STAR_SLOTS.length;
const TEXT_TRUNCATE_LENGTH = 280;
const VISIBLE_REVIEW_COUNT = 4;

function StarRow({ rating, size = 'sm' }: { rating?: number; size?: 'sm' | 'md' }) {
  const filled = Math.max(0, Math.min(MAX_STARS, Math.round(rating ?? 0)));
  const iconSizeClass = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';

  return (
    <div
      role="img"
      aria-label={`${filled} out of ${MAX_STARS} stars`}
      className="inline-flex items-center gap-0.5"
    >
      {STAR_SLOTS.map((slot, i) => (
        <FontAwesomeIcon
          key={slot}
          icon={i < filled ? faStarSolid : faStarRegular}
          className={cn(iconSizeClass, i < filled ? 'text-yellow-500' : 'text-gray-300')}
        />
      ))}
    </div>
  );
}

function ReviewCard({ review }: { review: GoogleReview }) {
  const [expanded, setExpanded] = useState(false);
  const text = review.body ?? '';
  const needsTruncation = text.length > TEXT_TRUNCATE_LENGTH;
  const displayed = expanded || !needsTruncation ? text : `${text.slice(0, TEXT_TRUNCATE_LENGTH)}…`;

  return (
    <article className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <header className="flex items-start gap-3">
        {review.authorPhotoUrl ? (
          <img
            src={review.authorPhotoUrl}
            alt={review.reviewerName ?? 'Reviewer'}
            className="h-10 w-10 rounded-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-sm font-medium text-gray-700">
            {getInitials(review.reviewerName)}
          </div>
        )}
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">
            {review.reviewerName ?? 'Google User'}
          </span>
          <span className="text-xs text-gray-500">{review.relativePublishTime}</span>
        </div>
      </header>
      <StarRow rating={review.rating} />
      {text && (
        <div className="text-sm leading-relaxed text-gray-700">
          <p className="whitespace-pre-line">{displayed}</p>
          {needsTruncation && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 text-xs font-medium text-blue-600 hover:underline"
            >
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      )}
    </article>
  );
}

export function GoogleReviewsSection() {
  const { props } = usePageSectionData<GoogleReviewsSectionProps>();
  const { placeId, businessName, rating, userRatingCount, googleMapsUri, reviews = [] } = props;

  if (!reviews.length) {
    return null;
  }

  const reviewCountLabel =
    typeof userRatingCount === 'number' ? userRatingCount.toLocaleString() : undefined;
  const googleReviewsHref = placeId
    ? `https://search.google.com/local/reviews?placeid=${placeId}`
    : googleMapsUri;
  const visibleReviews = reviews.slice(0, VISIBLE_REVIEW_COUNT);

  return (
    <section className="mx-auto w-full max-w-6xl px-4 py-10">
      <header className="mb-6 flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            {typeof rating === 'number' && (
              <>
                <span className="text-4xl font-semibold leading-none text-gray-900">
                  {rating.toFixed(1)}
                </span>
                <StarRow rating={rating} size="md" />
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            {reviewCountLabel ? <span>{reviewCountLabel} reviews on</span> : null}
            <span className="inline-flex items-center gap-1">
              <FontAwesomeIcon icon={faGoogle} className="h-4 w-4 text-[#4285F4]" />
              <span>Google</span>
            </span>
          </div>
          {businessName && <span className="text-sm text-gray-500">{businessName}</span>}
        </div>
      </header>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {visibleReviews.map((review, i) => (
          <ReviewCard key={review.providerReviewId ?? i} review={review} />
        ))}
      </div>
      {googleReviewsHref ? (
        <div className="mt-8 flex justify-center">
          <a
            href={googleReviewsHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            Load more reviews
          </a>
        </div>
      ) : null}
    </section>
  );
}
